/**
 * "To save sets, you must sign in." Sign in · Cancel — the user's own words (2026-10-27).
 *
 * The user, 2026-10-27: a heart signed out asks first — then "it should be in the centre of the screen,
 * not the bottom, without the header; and the MyRave logo". So not the
 * app's `Dialog` (a titled bottom sheet on a phone): a card of its own, centred at every width.
 * Mounted once by each shell; a screen opens it with `askBeforeSignIn` (`lib/signInFirst.ts`).
 * The Rideboard asks through it too (the 0.14.82 walk, 2026-11-02: "it's not using our design") —
 * signed out "Sign in to share a ride", and an account not yet registered for rides the one line and
 * "Complete my registration", which opens the wizard and comes back to the board.
 */
import { useEffect, useId, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { BrandLockup, Button } from '@/ui';
import { lockScroll } from '@/ui/Sheet';
import { useT } from '@/i18n';
import { askToSignIn, closeSignInAsk, onSignInAsked, signInAsked } from '@/lib/signInFirst';
import { leaveTo } from './AuthProvider';
import { rememberReturn } from './ReturnAfterSignIn';
import { appUrl } from '@/lib/tenant';
import './SignInAsk.css';

export function SignInAsk() {
  const asked = useSyncExternalStore(onSignInAsked, signInAsked, signInAsked);
  // On the page itself, above the shell's header and menu — inside the shell they stayed undimmed.
  return asked ? createPortal(<Ask then={asked.then} follow={asked.follow} rides={asked.rides} />, document.body) : null;
}

/** To the complete-registration wizard, coming back to this page afterwards (the Rideboard's door). */
function completeRegistration() {
  rememberReturn();
  void leaveTo(appUrl('/complete'));
}

function Ask({ then, follow, rides }: { then: Parameters<typeof askToSignIn>[0]; follow?: string; rides?: 'sign-in' | 'register' }) {
  const tr = useT();
  const textId = useId();
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => lockScroll(), []);
  useEffect(() => {
    card.current?.querySelector('button')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeSignInAsk(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="sign-in-ask" onClick={(e) => { if (e.target === e.currentTarget) closeSignInAsk(); }}>
      <div ref={card} role="dialog" aria-modal="true" aria-labelledby={textId} className="sign-in-ask-card">
        <BrandLockup size={30} orientation="stacked" />
        <p id={textId} className="sign-in-ask-text">
          {rides === 'register' ? tr('To use the Rideboard, complete your registration first.')
            : rides === 'sign-in' ? tr('Sign in to share a ride')
            : follow ? tr('Sign in to follow {name}', { name: follow }) : tr("To save sets, you must sign in.")}
        </p>
        <div className="sign-in-ask-actions">
          {rides === 'register'
            ? <Button variant="primary" onClick={() => { closeSignInAsk(); completeRegistration(); }}>{tr('Complete my registration')}</Button>
            : <Button variant="primary" onClick={() => { closeSignInAsk(); askToSignIn(then); }}>{tr('Sign in')}</Button>}
          <Button variant="quiet" onClick={closeSignInAsk}>{tr('Cancel')}</Button>
        </div>
      </div>
    </div>
  );
}
